import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Deposit.css';

function Deposit() {
  const [wallet, setWallet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositInfo, setDepositInfo] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USDT');
  const [selectedNetwork, setSelectedNetwork] = useState('ERC20');
  const [depositHistory, setDepositHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('deposit');
  const [availableNetworks, setAvailableNetworks] = useState({});
  const [manualDeposit, setManualDeposit] = useState({
    currency: 'USDT',
    amount: '',
    tx_hash: '',
    network: 'ERC20',
    from_address: ''
  });

  useEffect(() => {
    fetchWallet();
    fetchDepositHistory();
    fetchAvailableNetworks();
  }, []);

  const fetchWallet = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trcsite.net/api/wallet', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data.wallets || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      setLoading(false);
    }
  };

  const fetchDepositHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trcsite.net/api/wallet/deposits/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepositHistory(response.data.deposits || []);
    } catch (error) {
      console.error('Error fetching deposit history:', error);
    }
  };

  const fetchAvailableNetworks = async () => {
  try {
    console.log('🔄 Fetching available networks...');
    
    // Use the dedicated deposit addresses endpoint
    const response = await axios.get('https://trcsite.net/api/deposit/addresses');
    
    console.log('📡 API Response:', response.data);
    
    if (response.data.success && response.data.addresses) {
      // Organize networks by currency
      const networksByCurrency = {};
      response.data.addresses.forEach(addr => {
        if (!networksByCurrency[addr.currency]) {
          networksByCurrency[addr.currency] = [];
        }
        networksByCurrency[addr.currency].push(addr.network);
      });
      
      console.log('🗂️ Networks by currency:', networksByCurrency);
      setAvailableNetworks(networksByCurrency);
    } else {
      console.error('❌ No addresses in response');
      setAvailableNetworks({});
    }
  } catch (error) {
    console.error('❌ Error fetching available networks:', error);
    console.error('❌ Error details:', error.response?.data);
    setAvailableNetworks({});
  }
};

  const generateDepositAddress = async (currency, network = 'ERC20') => {
  try {
    // Remove token requirement for public endpoint
    const response = await axios.get('https://trcsite.net/api/deposit/addresses', {
      params: { currency, network }
    });
    
    console.log('Deposit address response:', response.data); // Debug log
    
    if (response.data.success && response.data.addresses.length > 0) {
      const walletInfo = response.data.addresses[0];
      
      setDepositInfo({
        address: walletInfo.address,
        network: walletInfo.network,
        currency: currency,
        min_deposit: walletInfo.min_deposit,
        memo: walletInfo.memo
      });
      
      setSelectedCurrency(currency);
      setSelectedNetwork(network);
      setActiveTab('address');
    } else {
      throw new Error(`No ${network} address configured for ${currency}`);
    }
  } catch (error) {
    console.error('Error getting deposit address:', error);
    alert('Failed to get deposit address: ' + (error.response?.data?.error || error.message));
  }
};

  const submitManualDeposit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://trcsite.net/api/wallet/deposit', 
        manualDeposit,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Deposit submitted successfully! It will be processed after confirmation.');
      setManualDeposit({
        currency: 'USDT',
        amount: '',
        tx_hash: '',
        network: 'ERC20',
        from_address: ''
      });
      fetchDepositHistory();
      fetchWallet();
    } catch (error) {
      console.error('Error submitting deposit:', error);
      alert('Failed to submit deposit: ' + (error.response?.data?.error || error.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#00c853';
      case 'pending': return '#ffa726';
      case 'rejected': return '#ff1744';
      default: return '#b0b8c4';
    }
  };

  const renderDepositButtons = (walletItem) => {
    const currency = walletItem.currency;
    const networks = availableNetworks[currency] || [];
    console.log(`Rendering deposit buttons for ${currency}:`, networks); // DEBUG
    
    if (networks.length === 0) {
      return (
        <button
          disabled
          style={{
            padding: '10px 20px',
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'not-allowed',
            width: '100%',
            fontSize: '14px'
          }}
        >
          No Address Available
        </button>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {networks.map(network => (
          <button
            key={network}
            onClick={() => generateDepositAddress(currency, network)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2962ff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#1e4fbf'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#2962ff'}
          >
            Deposit {network}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading wallet...</div>;
  }

  return (
    <div className="deposit-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '35px' }}>
      <div className="section-header-dp" style={{ marginBottom: '30px' }}>
        <h1>Deposit Funds</h1>
        <p style={{ color: '#b0b8c4' }}>Add funds to your trading account</p>
      </div>

      {/* Wallet Balances */}
      <div className="wallet-section" style={{ marginBottom: '40px' }}>
        <h2 style={{ color: '#ffffff', marginBottom: '20px' }}>Your Wallet Balances</h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
          gap: '15px',
          marginBottom: '30px',
        }}>
          {wallet.map((walletItem) => (
            <div key={walletItem.currency} style={{
              backgroundColor: '#1a2332',
              border: '1px solid #344258',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            >
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffffff', marginBottom: '10px' }}>
                {walletItem.currency}
              </div>
              <div style={{ fontSize: '24px', color: '#2962ff', marginBottom: '15px', fontWeight: 'bold' }}>
                {parseFloat(walletItem.balance).toFixed(8)}
              </div>
              {renderDepositButtons(walletItem)}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #344258',
          marginBottom: '20px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('deposit')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'deposit' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'deposit' ? '2px solid #2962ff' : 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              margin: '0 5px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'deposit') {
                e.target.style.backgroundColor = '#2a3b52';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'deposit') {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            Manual Deposit
          </button>
          <button
            onClick={() => setActiveTab('address')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'address' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'address' ? '2px solid #2962ff' : 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              margin: '0 5px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'address') {
                e.target.style.backgroundColor = '#2a3b52';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'address') {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            Deposit Address
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'history' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'history' ? '2px solid #2962ff' : 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              margin: '0 5px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'history') {
                e.target.style.backgroundColor = '#2a3b52';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'history') {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            Deposit History
          </button>
        </div>

        {/* Manual Deposit Form */}
        {activeTab === 'deposit' && (
          <div style={{
            backgroundColor: '#1a2332',
            border: '1px solid #344258',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            margin: '0 auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '20px', textAlign: 'center' }}>Manual Deposit</h3>
            <form onSubmit={submitManualDeposit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Currency
                </label>
                <select
                  value={manualDeposit.currency}
                  onChange={(e) => setManualDeposit({...manualDeposit, currency: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff',
                    transition: 'border-color 0.3s ease'
                  }}
                >
                  <option value="USDT">USDT</option>
                  <option value="BTC">BTC</option>
                  <option value="ETH">ETH</option>
                  <option value="BNB">BNB</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Amount
                </label>
                <input
                  type="number"
                  step="0.00000001"
                  value={manualDeposit.amount}
                  onChange={(e) => setManualDeposit({...manualDeposit, amount: e.target.value})}
                  placeholder="Enter amount"
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff',
                    transition: 'border-color 0.3s ease'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Transaction Hash
                </label>
                <input
                  type="text"
                  value={manualDeposit.tx_hash}
                  onChange={(e) => setManualDeposit({...manualDeposit, tx_hash: e.target.value})}
                  placeholder="Enter transaction hash"
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontFamily: 'monospace',
                    transition: 'border-color 0.3s ease'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  From Address (Optional)
                </label>
                <input
                  type="text"
                  value={manualDeposit.from_address}
                  onChange={(e) => setManualDeposit({...manualDeposit, from_address: e.target.value})}
                  placeholder="Your external wallet address (optional)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontFamily: 'monospace',
                    transition: 'border-color 0.3s ease'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Network
                </label>
                <select
                  value={manualDeposit.network}
                  onChange={(e) => setManualDeposit({...manualDeposit, network: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff',
                    transition: 'border-color 0.3s ease'
                  }}
                >
                  <option value="ERC20">ERC20 (Ethereum)</option>
                  <option value="TRC20">TRC20 (Tron)</option>
                  <option value="BEP20">BEP20 (Binance Smart Chain)</option>
                </select>
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#00c853',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#00b347'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#00c853'}
              >
                Submit Deposit
              </button>
            </form>
          </div>
        )}

        {/* Deposit Address */}
        {activeTab === 'address' && depositInfo && (
          <div style={{
            backgroundColor: '#1a2332',
            border: '1px solid #344258',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            margin: '0 auto',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '20px' }}>
              Deposit {depositInfo.currency} ({depositInfo.network})
            </h3>
            
            {/* Deposit Address */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                Your {depositInfo.network} Deposit Address:
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#2a3b52',
                border: '1px solid #344258',
                borderRadius: '4px',
                color: '#ffffff',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                marginBottom: '10px',
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                {depositInfo.address}
              </div>
              <button
                onClick={() => copyToClipboard(depositInfo.address)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2962ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1e4fbf'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2962ff'}
              >
                Copy Address
              </button>
            </div>

            {/* Memo/Tag (if applicable) */}
            {depositInfo.memo && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                  Memo/Tag (Required):
                </label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#2a3b52',
                  border: '1px solid #344258',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  marginBottom: '10px',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}>
                  {depositInfo.memo}
                </div>
                <button
                  onClick={() => copyToClipboard(depositInfo.memo)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2962ff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1e4fbf'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#2962ff'}
                >
                  Copy Memo
                </button>
              </div>
            )}

            {/* Important Information */}
            <div style={{
              backgroundColor: '#2a3b52',
              border: '1px solid #344258',
              borderRadius: '6px',
              padding: '15px',
              textAlign: 'left',
              marginTop: '20px'
            }}>
              <h4 style={{ color: '#ffa726', marginBottom: '10px', fontSize: '16px' }}>⚠️ Important Information:</h4>
              <ul style={{ color: '#b0b8c4', fontSize: '14px', paddingLeft: '20px', margin: 0, lineHeight: '1.5' }}>
                <li>Send only <strong style={{ color: '#ffffff' }}>{depositInfo.currency}</strong> to this address</li>
                <li>Network: <strong style={{ color: '#ffffff' }}>{depositInfo.network}</strong></li>
                <li>Minimum deposit: <strong style={{ color: '#00c853' }}>{depositInfo.min_deposit} {depositInfo.currency}</strong></li>
                <li>Deposits will be credited after confirmation</li>
                <li>Do not send other cryptocurrencies to this address</li>
                {depositInfo.memo && (
                  <li style={{ color: '#ffa726' }}>
                    <strong>Memo/Tag is REQUIRED</strong> for this deposit
                  </li>
                )}
                <li>Double-check the network before sending</li>
              </ul>
            </div>
          </div>
        )}

        {/* Deposit History */}
        {activeTab === 'history' && (
          <div>
            <h3 style={{ color: '#ffffff', marginBottom: '20px' }}>Deposit History</h3>
            {depositHistory.length > 0 ? (
              <div style={{ overflowX: 'auto', borderRadius: '8px' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse', 
                  backgroundColor: '#1a2332', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#2a3b52' }}>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4', fontWeight: '600' }}>Currency</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4', fontWeight: '600' }}>Amount</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4', fontWeight: '600' }}>Network</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4', fontWeight: '600' }}>TX Hash</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4', fontWeight: '600' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depositHistory.map((deposit) => (
                      <tr key={deposit.id} style={{ borderBottom: '1px solid #344258', transition: 'background-color 0.3s ease' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2a3b52'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                        <td style={{ padding: '15px', fontWeight: 'bold', color: '#ffffff' }}>{deposit.currency}</td>
                        <td style={{ padding: '15px', color: '#ffffff' }}>{parseFloat(deposit.amount).toFixed(8)}</td>
                        <td style={{ padding: '15px', color: '#b0b8c4' }}>
                          <span style={{
                            padding: '4px 8px',
                            backgroundColor: '#2962ff',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {deposit.network}
                          </span>
                        </td>
                        <td style={{ padding: '15px', fontFamily: 'monospace', fontSize: '12px', color: '#b0b8c4' }}>
                          {deposit.tx_hash ? 
                            `${deposit.tx_hash.slice(0, 10)}...${deposit.tx_hash.slice(-8)}` : 
                            'N/A'
                          }
                        </td>
                        <td style={{ padding: '15px' }}>
                          <span style={{
                            color: getStatusColor(deposit.status),
                            fontWeight: 'bold',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: `${getStatusColor(deposit.status)}20`,
                            fontSize: '12px'
                          }}>
                            {deposit.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '15px', color: '#b0b8c4' }}>
                          {new Date(deposit.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ 
                color: '#b0b8c4', 
                textAlign: 'center', 
                padding: '40px',
                backgroundColor: '#1a2332',
                border: '1px solid #344258',
                borderRadius: '8px',
                fontSize: '16px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>📝</div>
                No deposit history found
              </div>
            )}
          </div>
        )}

        {/* No Address Selected */}
        {activeTab === 'address' && !depositInfo && (
          <div style={{
            backgroundColor: '#1a2332',
            border: '1px solid #344258',
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔍</div>
            <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>No Deposit Address Selected</h3>
            <p style={{ color: '#b0b8c4' }}>
              Please select a currency and network from your wallet balances above to generate a deposit address.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Deposit;